<script lang="ts" context="module">
	export function getRandomHsl() {
		const r = Math.floor(Math.random() * 256);
		const g = Math.floor(Math.random() * 256);
		const b = Math.floor(Math.random() * 256);
		return colorConvert.rgb.hsl(r, g, b);
	}
</script>

<script lang="ts">
	import colorConvert from 'color-convert';
	import { type HSL } from 'color-convert/conversions';

	export let name: string;
	export let hsl: HSL;
	export let alpha: number | undefined = undefined;
</script>

<div class="flex flex-nowrap gap-2">
	<div class="grow flex flex-col justify-between">
		<span class="text-sm font-medium ml-1"> Hue </span>
		<input
			id="hue-{name}"
			type="range"
			min="0"
			max="360"
			step="1"
			bind:value={hsl[0]}
			class="w-full h-8 rounded-md appearance-none cursor-pointer border border-base-200 hue"
			on:input
		/>
	</div>
	<input
		type="number"
		bind:value={hsl[0]}
		min="0"
		max="360"
		step="1"
		class="self-end flex-shrink-0 w-20 input input-bordered input-sm border-base-200 rounded-md"
		on:input
	/>
</div>

<div class="flex flex-nowrap gap-2">
	<div class="grow flex flex-col justify-between">
		<span class="text-sm font-medium ml-1"> Saturation </span>
		<input
			id="saturation-{name}"
			type="range"
			min="0"
			max="100"
			step="1"
			bind:value={hsl[1]}
			class="w-full h-8 rounded-md appearance-none cursor-pointer border border-base-200 saturation"
			style="--h: {hsl[0]};"
			on:input
		/>
	</div>
	<input
		type="number"
		bind:value={hsl[1]}
		min="0"
		max="100"
		step="1"
		class="self-end flex-shrink-0 w-20 input input-bordered input-sm border-base-200 rounded-md"
		on:input
	/>
</div>

<div class="flex flex-nowrap gap-2">
	<div class="grow flex flex-col justify-between">
		<span class="text-sm font-medium ml-1"> Lightness </span>
		<input
			id="lightness-{name}"
			type="range"
			min="0"
			max="100"
			step="1"
			bind:value={hsl[2]}
			class="w-full h-8 rounded-md appearance-none cursor-pointer border border-base-200 lightness"
			style="--hsl: {hsl[0]} {hsl[1]} {hsl[2]};"
			on:input
		/>
	</div>
	<input
		type="number"
		bind:value={hsl[2]}
		min="0"
		max="100"
		step="1"
		class="self-end flex-shrink-0 w-20 input input-bordered input-sm border-base-200 rounded-md"
		on:input
	/>
</div>

{#if alpha}
	<div class="flex flex-nowrap gap-2">
		<div class="grow flex flex-col justify-between">
			<span class="text-sm font-medium ml-1"> Alpha (opacity) </span>
			<input
				id="alpha-{name}"
				type="range"
				bind:value={alpha}
				class="w-full h-8 rounded-md appearance-none cursor-pointer border border-base-200"
				on:input
				min="0"
				max="1"
				step="0.01"
			/>
		</div>
		<input
			type="number"
			bind:value={alpha}
			min="0"
			max="1"
			step="0.01"
			class="self-end flex-shrink-0 w-20 input input-bordered input-sm border-base-200 rounded-md"
			on:input
		/>
	</div>
{/if}

<style lang="postcss">
	.hue {
		background: #ff0000; /* Fallback color for older browsers */
		background: -webkit-linear-gradient(
			left,
			rgb(255, 0, 0),
			rgb(255, 255, 0),
			rgb(0, 255, 0),
			rgb(0, 255, 255),
			rgb(0, 0, 255),
			rgb(255, 0, 255),
			rgb(255, 0, 0)
		); /* For Safari 5.1 to 6.0 */
		background: -o-linear-gradient(
			left,
			rgb(255, 0, 0),
			rgb(255, 255, 0),
			rgb(0, 255, 0),
			rgb(0, 255, 255),
			rgb(0, 0, 255),
			rgb(255, 0, 255),
			rgb(255, 0, 0)
		); /* For Opera 11.1 to 12.0 */
		background: -moz-linear-gradient(
			left,
			rgb(255, 0, 0),
			rgb(255, 255, 0),
			rgb(0, 255, 0),
			rgb(0, 255, 255),
			rgb(0, 0, 255),
			rgb(255, 0, 255),
			rgb(255, 0, 0)
		); /* For Firefox 3.6 to 15 */
		background: linear-gradient(
			to right,
			rgb(255, 0, 0),
			rgb(255, 255, 0),
			rgb(0, 255, 0),
			rgb(0, 255, 255),
			rgb(0, 0, 255),
			rgb(255, 0, 255),
			rgb(255, 0, 0)
		); /* Standard syntax */
	}

	.saturation {
		background: hsl(var(--h), 0%, 50%); /* Fallback color for older browsers */
		background: -webkit-linear-gradient(
			left,
			hsl(var(--h), 0%, 50%),
			hsl(var(--h), 100%, 50%)
		); /* For Safari 5.1 to 6.0 */
		background: -moz-linear-gradient(
			left,
			hsl(var(--h), 0%, 50%),
			hsl(var(--h), 100%, 50%)
		); /* For Firefox 3.6 to 15 */
		background: -o-linear-gradient(
			left,
			hsl(var(--h), 0%, 50%),
			hsl(var(--h), 100%, 50%)
		); /* For Opera 11.1 to 12.0 */
		background: linear-gradient(
			to right,
			hsl(var(--h), 0%, 50%),
			hsl(var(--h), 100%, 50%)
		); /* Standard syntax */
	}

	.lightness {
		background: rgb(0, 0, 0); /* Fallback color for older browsers */
		background: -webkit-linear-gradient(
			left,
			rgb(0, 0, 0),
			hsl(var(--hsl)),
			rgb(255, 255, 255)
		); /* For Safari 5.1 to 6.0 */
		background: -moz-linear-gradient(
			left,
			rgb(0, 0, 0),
			hsl(var(--hsl)),
			rgb(255, 255, 255)
		); /* For Firefox 3.6 to 15 */
		background: -o-linear-gradient(
			left,
			rgb(0, 0, 0),
			hsl(var(--hsl)),
			rgb(255, 255, 255)
		); /* For Opera 11.1 to 12.0 */
		background: linear-gradient(
			to right,
			rgb(0, 0, 0),
			hsl(var(--hsl)),
			rgb(255, 255, 255)
		); /* Standard syntax */
	}

	/* WebKit browsers (Chrome, Safari) */
	input[type='range']::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		@apply w-6 h-6 bg-base-200 rounded-md border border-base-200;
	}

	/* Firefox */
	input[type='range']::-moz-range-thumb {
		@apply w-6 h-6 bg-base-200 rounded-md border border-base-200;
	}

	/* Internet Explorer */
	input[type='range']::-ms-thumb {
		@apply w-6 h-6 bg-base-200 rounded-md border border-base-200;
	}
</style>
